import React, { useState, useEffect, useCallback } from "react";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { Modal, Button } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import swal from "sweetalert";
import {
  ZonaSocialSelect,
  useZonaSocialOptions,
} from "../../components/ZonaSocial";
import CategoriaCreateModal from "./CategoriaCreateModal";

const ACTIVOS_URL = "/api/v3/zonas-sociales/activos/";
const CATEGORIAS_URL = "/api/v3/zonas-sociales/activos/categorias/";

const activoSchema = Yup.object().shape({
  nombre: Yup.string().required("El nombre es obligatorio."),
  cantidad: Yup.number()
    .integer("Debe ser un número entero.")
    .min(1, "Debe ser al menos 1.")
    .required("La cantidad es obligatoria."),
  estado: Yup.string().required("El estado es obligatorio."),
  costo: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value,
    ),
});

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const parseDateFromAPI = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toISOString().split("T")[0];
};

const ActivoCreateForm = ({ show, onHide, onSuccess, activeActivo }) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [loadedCategorias, setLoadedCategorias] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [activoData, setActivoData] = useState(null);

  const isEditing = !!activeActivo;

  const fetchActivoData = useCallback(async () => {
    if (!isEditing || !activeActivo?.uuid) return;

    setLoadingData(true);
    try {
      const response = await axiosInstance.get(
        `${ACTIVOS_URL}${activeActivo.uuid}/`,
      );
      setActivoData(response.data);
    } catch (error) {
      console.error("Error fetching activo data:", error);
      swal("Error", "No fue posible cargar los datos del activo.", "error");
    } finally {
      setLoadingData(false);
    }
  }, [isEditing, activeActivo?.uuid]);

  useEffect(() => {
    if (show && isEditing) {
      fetchActivoData();
      fetchCategorias();
    } else if (show) {
      fetchCategorias();
    }
  }, [show, isEditing]);

  const initialValues = activoData
    ? {
        nombre: activoData.nombre || "",
        cantidad: activoData.cantidad || 1,
        categoria_id: activoData.categoria?.id || activoData.categoria_id || "",
        estado: activoData.estado || "nuevo",
        zona_uuid:
          activoData.zona?.uuid ||
          activoData.zona_uuid ||
          activoData.zona ||
          "",
        responsable_id:
          activoData.responsable?.id || activoData.responsable_id || "",
        costo: activoData.costo || "",
        fecha_adquisicion: parseDateFromAPI(activoData.fecha_adquisicion),
        garantia_hasta: parseDateFromAPI(activoData.garantia_hasta),
        observaciones: activoData.observaciones || "",
      }
    : isEditing
      ? {
          nombre: activeActivo.nombre || "",
          cantidad: activeActivo.cantidad || 1,
          categoria_id:
            activeActivo.categoria?.id || activeActivo.categoria || "",
          estado: activeActivo.estado || "nuevo",
          zona_uuid: activeActivo.zona?.uuid || activeActivo.zona || "",
          responsable_id: activeActivo.responsable?.id || "",
          costo: activeActivo.costo || "",
          fecha_adquisicion: "",
          garantia_hasta: "",
          observaciones: activeActivo.observaciones || "",
        }
      : {
          nombre: "",
          cantidad: 1,
          categoria_id: "",
          estado: "nuevo",
          zona_uuid: "",
          responsable_id: "",
          costo: "",
          fecha_adquisicion: "",
          garantia_hasta: "",
          observaciones: "",
        };

  const { zones, isLoading: isLoadingZones } = useZonaSocialOptions({
    onError: () =>
      swal(
        "Error",
        "No fue posible cargar las zonas sociales. Inténtalo de nuevo.",
        "error",
      ),
  });

  const fetchCategorias = async () => {
    if (loadedCategorias) return;
    try {
      const response = await axiosInstance.get(CATEGORIAS_URL);
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setCategorias(results);
      setLoadedCategorias(true);
    } catch (error) {
      swal(
        "Error",
        "No fue posible cargar las categorías de activos.",
        "error",
      );
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      fetchCategorias();
    }
  }, [show]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        cantidad: Number(values.cantidad),
        categoria_id: values.categoria_id || null,
        zona_uuid: values.zona_uuid || null,
        responsable_id: values.responsable_id || null,
        costo:
          values.costo === "" || values.costo === null
            ? null
            : Number(values.costo),
        fecha_adquisicion: formatDateForAPI(values.fecha_adquisicion),
        garantia_hasta: formatDateForAPI(values.garantia_hasta),
      };

      if (isEditing) {
        await axiosInstance.patch(
          `${ACTIVOS_URL}${activeActivo.uuid}/`,
          payload,
        );
        swal("Éxito", "Activo actualizado correctamente.", "success");
      } else {
        await axiosInstance.post(ACTIVOS_URL, payload);
        swal("Éxito", "Activo creado correctamente.", "success");
      }

      if (typeof onSuccess === "function") {
        onSuccess();
      }
      if (typeof onHide === "function") {
        onHide();
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        if (error.response.data) {
          setErrors(error.response.data);
        } else {
          swal(
            "Error",
            "La solicitud no es válida. Por favor, verifica los campos.",
            "error",
          );
        }
      } else {
        swal(
          "Error",
          "Ha ocurrido un error inesperado. Por favor, intenta nuevamente más tarde.",
          "error",
        );
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing
              ? `Editar activo: ${activeActivo?.consecutivo || ""}`
              : "Crear activo"}
          </Modal.Title>
        </Modal.Header>
        {loadingData ? (
          <Modal.Body>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Cargando datos del activo...</p>
            </div>
          </Modal.Body>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={activoSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Modal.Body>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="nombre">
                          Nombre <span className="text-danger">*</span>
                        </label>
                        <Field
                          name="nombre"
                          id="nombre"
                          className={`form-control${errors.nombre && touched.nombre ? " is-invalid" : ""}`}
                        />
                        {errors.nombre && touched.nombre && (
                          <div className="invalid-feedback">
                            {errors.nombre}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group">
                        <label htmlFor="cantidad">
                          Cantidad <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="number"
                          min="1"
                          name="cantidad"
                          id="cantidad"
                          className={`form-control${errors.cantidad && touched.cantidad ? " is-invalid" : ""}`}
                        />
                        {errors.cantidad && touched.cantidad && (
                          <div className="invalid-feedback">
                            {errors.cantidad}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group">
                        <label htmlFor="estado">
                          Estado <span className="text-danger">*</span>
                        </label>
                        <Field
                          as="select"
                          name="estado"
                          id="estado"
                          className={`form-select${errors.estado && touched.estado ? " is-invalid" : ""}`}
                        >
                          <option value="nuevo">Nuevo</option>
                          <option value="bueno">Bueno</option>
                          <option value="reparacion">En reparación</option>
                          <option value="dado_de_baja">Dado de baja</option>
                        </Field>
                        {errors.estado && touched.estado && (
                          <div className="invalid-feedback">
                            {errors.estado}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="categoria_id">Categoría</label>
                        <div className="d-flex gap-2">
                          <select
                            id="categoria_id"
                            name="categoria_id"
                            className="form-select"
                            value={values.categoria_id}
                            onChange={handleChange}
                            onFocus={fetchCategorias}
                          >
                            <option value="">Sin categoría</option>
                            {categorias.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.nombre}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setShowCategoryModal(true)}
                            title="Crear nueva categoría"
                          >
                            <i className="fas fa-plus" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <ZonaSocialSelect
                        value={values.zona_uuid || ""}
                        onChange={(newValue) =>
                          setFieldValue("zona_uuid", newValue || "")
                        }
                        allowAllOption={false}
                        allowEmptyOption
                        showLabel
                        label="Zona social (ubicación)"
                        options={zones}
                        loading={isLoadingZones}
                        autoFetch={false}
                        disabled={isLoadingZones || loading}
                      />
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="costo">Costo</label>
                        <Field
                          type="number"
                          step="0.01"
                          name="costo"
                          id="costo"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="fecha_adquisicion">
                          Fecha de adquisición
                        </label>
                        <Field
                          type="date"
                          name="fecha_adquisicion"
                          id="fecha_adquisicion"
                          className={`form-control${errors.fecha_adquisicion && touched.fecha_adquisicion ? " is-invalid" : ""}`}
                        />
                        {errors.fecha_adquisicion &&
                          touched.fecha_adquisicion && (
                            <div className="invalid-feedback">
                              {errors.fecha_adquisicion}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="garantia_hasta">
                          Garantía hasta (fecha)
                        </label>
                        <Field
                          type="date"
                          name="garantia_hasta"
                          id="garantia_hasta"
                          className={`form-control${errors.garantia_hasta && touched.garantia_hasta ? " is-invalid" : ""}`}
                        />
                        {errors.garantia_hasta && touched.garantia_hasta && (
                          <div className="invalid-feedback">
                            {errors.garantia_hasta}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="observaciones">Observaciones</label>
                        <Field
                          as="textarea"
                          rows="3"
                          name="observaciones"
                          id="observaciones"
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer className="border-top-0">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={isSubmitting || loading}
                    onClick={onHide}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={isSubmitting || loading}
                  >
                    {loading ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <i
                          className="fas fa-circle-notch fa-spin"
                          aria-hidden="true"
                        />
                        <span>Guardando...</span>
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-save me-2" />
                        {isEditing ? "Actualizar" : "Crear"}
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </form>
            )}
          </Formik>
        )}
      </Modal>
      <CategoriaCreateModal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        onSuccess={(newCategory) => {
          setCategorias((prev) => [newCategory, ...prev]);
        }}
      />
    </>
  );
};

export default ActivoCreateForm;
